export type TrainingAudience =
    | 'GENERAL'
    | 'STORE_MANAGER'
    | 'DIVISION_MANAGER'
    | 'OPS_AREA_EXECUTIVE'
    | 'PROCUREMENT_MANAGER'
    | 'BUSINESS_LEADER'
    | 'DATA_STRATEGIST'
    | 'ADMIN';

export const TRAINING_AUDIENCE_OPTIONS: {
    value: TrainingAudience;
    label: string;
}[] = [
    { value: 'GENERAL', label: 'General (everyone)' },
    { value: 'STORE_MANAGER', label: 'Store Manager' },
    { value: 'DIVISION_MANAGER', label: 'Divisional Manager' },
    { value: 'OPS_AREA_EXECUTIVE', label: 'Ops Executive' },
    { value: 'PROCUREMENT_MANAGER', label: 'Procurement Manager' },
    { value: 'BUSINESS_LEADER', label: 'Business Leader' },
    { value: 'DATA_STRATEGIST', label: 'Data Strategist' },
    { value: 'ADMIN', label: 'Admin' },
];

export const TRAINING_AUDIENCE_LABELS: Record<TrainingAudience, string> =
    Object.fromEntries(
        TRAINING_AUDIENCE_OPTIONS.map(option => [option.value, option.label]),
    ) as Record<TrainingAudience, string>;

export const TRAINING_AUDIENCE_BADGES: Record<
    TrainingAudience,
    { color: string; label: string }
> = {
    GENERAL: { color: 'gray', label: 'General (everyone)' },
    STORE_MANAGER: { color: 'cyan', label: 'Store Manager' },
    DIVISION_MANAGER: { color: 'red', label: 'Divisional Manager' },
    OPS_AREA_EXECUTIVE: { color: 'yellow', label: 'Ops Executive' },
    PROCUREMENT_MANAGER: { color: 'orange', label: 'Procurement Manager' },
    BUSINESS_LEADER: { color: 'violet', label: 'Business Leader' },
    DATA_STRATEGIST: { color: 'blue', label: 'Data Strategist' },
    ADMIN: { color: 'green', label: 'Admin' },
};

export function getTrainingAudienceBadge(audience: TrainingAudience): {
    color: string;
    label: string;
} {
    return (
        TRAINING_AUDIENCE_BADGES[audience] ?? {
            color: 'gray',
            label: audience,
        }
    );
}

export interface TrainingCategory {
    id: number;
    name: string;
    description?: string | null;
    sortOrder: number;
    audiences: TrainingAudience[];
    createdAt?: string;
    updatedAt?: string;
}

export interface TrainingMaterial {
    id: number;
    title: string;
    description?: string | null;
    s3Key: string;
    thumbnailS3Key?: string | null;
    thumbnailUrl?: string | null;
    fileName: string;
    fileSize: number;
    fileType: string;
    categoryId: number;
    category?: Pick<TrainingCategory, 'id' | 'name' | 'audiences'> | null;
    uploadedById: string;
    uploadedBy?: {
        id: string;
        firstName: string;
        lastName: string;
    } | null;
    createdAt: string;
    updatedAt: string;
}

export const TRAINING_ALLOWED_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'video/mp4',
    'video/webm',
] as const;

export type TrainingAllowedMimeType =
    (typeof TRAINING_ALLOWED_MIME_TYPES)[number];

const EXTENSION_TO_MIME: Record<string, TrainingAllowedMimeType> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    pdf: 'application/pdf',
    mp4: 'video/mp4',
    webm: 'video/webm',
};

const MIME_ALIASES: Record<string, TrainingAllowedMimeType> = {
    'image/jpg': 'image/jpeg',
};

export function resolveTrainingFileType(file: File): string | null {
    const rawType = file.type.trim().toLowerCase();
    if (
        TRAINING_ALLOWED_MIME_TYPES.includes(rawType as TrainingAllowedMimeType)
    ) {
        return rawType;
    }

    const aliased = MIME_ALIASES[rawType];
    if (aliased) {
        return aliased;
    }

    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    return EXTENSION_TO_MIME[extension] ?? null;
}

export function isTrainingVideoFile(file: File, mimeType?: string): boolean {
    const resolved = mimeType ?? resolveTrainingFileType(file);
    if (resolved?.startsWith('video/')) {
        return true;
    }
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    return ['mp4', 'webm'].includes(extension);
}

export const TRAINING_MAX_IMAGE_PDF_BYTES = 50 * 1024 * 1024;
export const TRAINING_MAX_VIDEO_BYTES = 500 * 1024 * 1024;
